import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'react-feather';

import { useLocation, useNavigate } from 'react-router-dom';

import { DillDispute } from '@dill/dill-shared';
import { useAppSelector, useAppDispatch } from '../../../../../redux/store';
import { getSupplierDisputes, updateDispute } from '../../../../../redux/services/disputesService';
import { DillButton } from '../../../../general/Dill';
import SupplierDisputesTable from '../../../suppliers/SupplierDisputes/SupplierDisputesTable';
import { DisputeDetailsModal } from '../../../suppliers/SupplierDisputes/DisputeDetailsModal';
import { CreateDisputeModal } from '../../../suppliers/SupplierDisputes/CreateDisputeModal';

function SupplierCustomerDisputes({
  buyerId,
  selectedJobIds,
}: {
  buyerId: string;
  selectedJobIds?: string[];
}) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { disputes } = useAppSelector((state) => state.disputes);
  const { supplierTransactions } = useAppSelector((state) => state.payments);

  const disputeIdToView = new URLSearchParams(location.search).get('disputeId') || undefined;

  const [selectedDispute, setSelectedDispute] = useState<DillDispute | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getSupplierDisputes({ supplierId: currentSupplier.id, buyerId }));
    }
  }, [currentSupplier?.id, buyerId, dispatch]);

  useEffect(() => {
    if (disputeIdToView && disputes.length > 0) {
      const dispute = disputes.find((d) => d.id === disputeIdToView);
      if (dispute) {
        setSelectedDispute(dispute);
        setIsModalOpen(true);
      }
    }
  }, [disputeIdToView, disputes]);

  const filteredDisputes = useMemo(() => {
    const activeJobIds = (selectedJobIds ?? []).filter((id) => id !== 'ALL');
    if (activeJobIds.length === 0 || supplierTransactions.length === 0) return disputes;
    const transactionIdSet = new Set(
      supplierTransactions
        .filter((t) => t.invoicePayments.some((ip) => ip.jobId && activeJobIds.includes(ip.jobId)))
        .map((t) => t.id)
    );
    return disputes.filter((d) => d.transactionId && transactionIdSet.has(d.transactionId));
  }, [disputes, selectedJobIds, supplierTransactions]);

  const handleMarkResolved = async (disputeId: string) => {
    if (currentSupplier?.id) {
      await dispatch(
        updateDispute({ supplierId: currentSupplier.id, disputeId, isResolved: true })
      );
      dispatch(getSupplierDisputes({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  const handleRowClick = (dispute: DillDispute) => {
    setSelectedDispute(dispute);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDispute(null);
    const params = new URLSearchParams(location.search);
    if (params.has('disputeId')) {
      params.delete('disputeId');
      navigate({ search: params.toString() }, { replace: true });
    }
  };

  const handleDisputeCreated = () => {
    if (currentSupplier?.id) {
      dispatch(getSupplierDisputes({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  return (
    <div className="w-full h-full pb-4 pt-4">
      <SupplierDisputesTable
        disputes={filteredDisputes}
        onMarkResolved={handleMarkResolved}
        onRowClick={handleRowClick}
        hideCustomerColumn
        toolbarRight={
          <DillButton
            label="New Dispute"
            icon={<Plus size={14} />}
            onClick={() => setIsCreateModalOpen(true)}
            height={31}
          />
        }
      />

      <DisputeDetailsModal
        open={isModalOpen}
        dispute={selectedDispute}
        onClose={handleCloseModal}
        onMarkResolved={handleMarkResolved}
      />

      <CreateDisputeModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        buyerId={buyerId}
        onCreated={handleDisputeCreated}
      />
    </div>
  );
}

export default SupplierCustomerDisputes;
